import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, Pencil, Star, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatPriceINR, formatKm, coverImage } from "../lib/format";
import SafeImage from "../components/SafeImage";

const STATUS_OPTS = [
  { key: "", label: "All" },
  { key: "needs_review", label: "Needs Review" },
  { key: "available", label: "Available" },
  { key: "reserved", label: "Reserved" },
  { key: "sold", label: "Sold" },
  { key: "coming_soon", label: "Coming Soon" },
  { key: "archived", label: "Archived" },
];

const AdminInventory = () => {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [brand, setBrand] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, label }
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const r = await api.get(`/admin/vehicles?${params.toString()}`);
    setItems(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const brandOptions = React.useMemo(
    () => Array.from(new Set(items.map((v) => v.brand).filter(Boolean))).sort(),
    [items]
  );
  const visibleItems = React.useMemo(
    () => (brand ? items.filter((v) => v.brand === brand) : items),
    [items, brand]
  );

  const duplicate = async (id) => {
    try { await api.post(`/admin/vehicles/${id}/duplicate`); toast.success("Duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };
  const requestDelete = (v) => {
    setPendingDelete({ id: v.id, label: `${v.manufacturing_year} ${v.brand} ${v.model}${v.variant ? " " + v.variant : ""}` });
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/vehicles/${pendingDelete.id}`);
      toast.success("Vehicle deleted");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };
  const setStatusOf = async (id, newStatus) => {
    try { await api.put(`/admin/vehicles/${id}`, { status: newStatus }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const toggleFeature = async (v) => {
    try { await api.put(`/admin/vehicles/${v.id}`, { is_featured: !v.is_featured }); load(); }
    catch { toast.error("Failed"); }
  };
  const setSegmentOf = async (v, seg) => {
    try {
      await api.put(`/admin/vehicles/${v.id}`, { segment: seg });
      setItems((prev) => prev.map((x) => (x.id === v.id ? { ...x, segment: seg } : x)));
      toast.success(seg === "luxury" ? "Marked as Luxury" : "Marked as Standard Premium");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#7A1519]">Inventory</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-[#111111]">Vehicles</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/imports/cartrade" className="border hairline bg-white text-[#111111] px-4 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2 hover:border-[#7A1519] hover:text-[#7A1519] transition-colors" data-testid="admin-import-cartrade-btn">
            <FileSpreadsheet size={14} /> Import CarTrade Inventory
          </Link>
          <Link to="/admin/inventory/new" className="bg-[#7A1519] hover:bg-[#5a0f12] text-white px-5 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="admin-add-vehicle">
            <Plus size={14} /> Add Vehicle
          </Link>
        </div>
      </div>

      <div className="bg-white border hairline p-4 rounded-sm flex gap-3 flex-wrap items-center">
        <div className="flex gap-1 flex-wrap">
          {STATUS_OPTS.map((s) => (
            <button key={s.key || "all"} onClick={() => setStatus(s.key)} className={`text-xs px-3 py-1.5 rounded-sm border hairline ${status === s.key ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`} data-testid={`filter-status-${s.key || "all"}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[220px]">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search brand, model, variant…" className="w-full bg-[#F7F6F3] border hairline px-3 py-2 rounded-sm text-sm" data-testid="admin-inv-search" />
        </div>
        <select value={brand} onChange={(e) => setBrand(e.target.value)} className="bg-white border hairline px-3 py-2 rounded-sm text-sm min-w-[160px]" data-testid="admin-inv-brand-filter">
          <option value="">All brands</option>
          {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <button onClick={load} className="border hairline px-4 py-2 rounded-sm text-sm">Search</button>
      </div>

      <div className="bg-white border hairline rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F3] border-b hairline">
            <tr className="text-left text-[#242424]/60">
              <th className="px-4 py-3 font-medium">Vehicle</th>
              <th className="px-4 py-3 font-medium">Price</th>
              <th className="px-4 py-3 font-medium">Km</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Segment</th>
              <th className="px-4 py-3 font-medium">Featured</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-[#242424]/60">Loading…</td></tr>}
            {!loading && visibleItems.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#242424]/60">No vehicles yet.</td></tr>}
            {visibleItems.map((v) => (
              <tr key={v.id} className="border-b hairline hover:bg-[#F7F6F3]/50" data-testid={`inv-row-${v.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-16 aspect-[4/3] bg-[#F7F6F3] overflow-hidden rounded-sm shrink-0">
                      <SafeImage src={coverImage(v)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="font-medium text-[#111111]">{v.manufacturing_year} {v.brand} {v.model}</div>
                      <div className="text-xs text-[#242424]/60">{v.variant}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#7A1519] font-semibold">{formatPriceINR(v.price)}</td>
                <td className="px-4 py-3">{formatKm(v.kilometres)}</td>
                <td className="px-4 py-3">
                  <select value={v.status} onChange={(e) => setStatusOf(v.id, e.target.value)} className="bg-white border hairline px-2 py-1 rounded-sm text-xs" data-testid={`inv-status-${v.slug}`}>
                    {STATUS_OPTS.filter(o => o.key).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={v.segment === "luxury" ? "luxury" : "standard_premium"} onChange={(e) => setSegmentOf(v, e.target.value)} className="bg-white border hairline px-2 py-1 rounded-sm text-xs" data-testid={`inv-segment-${v.slug}`}>
                    <option value="standard_premium">Standard Premium</option>
                    <option value="luxury">Luxury</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleFeature(v)} className={`p-1.5 border hairline rounded-sm ${v.is_featured ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`} data-testid={`inv-feature-${v.slug}`}>
                    <Star size={14} fill={v.is_featured ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => nav(`/admin/inventory/${v.id}`)} className="p-2 border hairline rounded-sm hover:bg-[#F7F6F3]" data-testid={`inv-edit-${v.slug}`}><Pencil size={14} /></button>
                    <button onClick={() => duplicate(v.id)} className="p-2 border hairline rounded-sm hover:bg-[#F7F6F3]" data-testid={`inv-dup-${v.slug}`}><Copy size={14} /></button>
                    <button onClick={() => requestDelete(v)} className="p-2 border hairline rounded-sm hover:bg-[#F7F6F3] text-[#7A1519]" data-testid={`inv-del-${v.slug}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-sm max-w-md w-full p-6 border hairline shadow-xl">
            <h3 className="font-display text-xl font-semibold text-[#111111]">Delete vehicle?</h3>
            <p className="text-sm text-[#242424]/80 mt-2">
              This will permanently remove <span className="font-medium text-[#111111]">{pendingDelete.label}</span> from the inventory. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setPendingDelete(null)} disabled={deleting} className="border hairline bg-white text-[#111111] px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="delete-cancel-btn">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="bg-[#7A1519] hover:bg-[#5a0f12] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="delete-confirm-btn">
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInventory;
